/** Comma-separated membership IDs; null/empty = visible to all membership types. */
export function parseAllowedMembershipIds(
  allowedMembershipIds?: string | null,
): number[] {
  if (!allowedMembershipIds?.trim()) return [];
  return allowedMembershipIds
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);
}

export function isEventOpenToAllMemberships(
  allowedMembershipIds?: string | null,
): boolean {
  return parseAllowedMembershipIds(allowedMembershipIds).length === 0;
}

export function serializeAllowedMembershipIds(
  selectedIds: number[],
  allMembershipIds: number[],
): string | null | undefined {
  const selected = Array.from(
    new Set(selectedIds.filter((id) => id > 0)),
  ).sort((a, b) => a - b);
  if (selected.length === 0) return undefined;
  if (
    allMembershipIds.length > 0 &&
    selected.length >= allMembershipIds.length &&
    allMembershipIds.every((id) => selected.includes(id))
  ) {
    return null;
  }
  return selected.join(",");
}

/** "Youth Membership", "Early Stage Membership", and short forms resolve to the same youth tier key. */
export function normalizeMembershipKey(name: string): string {
  let t = name.trim();
  if (/\s+membership\s*$/i.test(t)) {
    t = t.replace(/\s+membership\s*$/i, "").trim();
  }
  const key = t.toLowerCase();
  if (key === "youth" || key === "early stage") return "youth";
  return key;
}

function findMembershipByUserName(
  memberships: readonly { id: number; name: string }[],
  userMembershipName: string,
): { id: number; name: string } | null {
  const trimmed = userMembershipName.trim();
  const exact = memberships.find((m) => m.name === trimmed);
  if (exact) return exact;
  const lower = trimmed.toLowerCase();
  const ci = memberships.find((m) => m.name.trim().toLowerCase() === lower);
  if (ci) return ci;
  const userKey = normalizeMembershipKey(trimmed);
  const byKey = memberships.filter(
    (m) => normalizeMembershipKey(m.name) === userKey,
  );
  return byKey.length === 1 ? byKey[0] : null;
}

/** Events only: Youth + Early Stage share one visibility tier (matches API). */
function getYouthTierMembershipIds(
  memberships: readonly { id: number; name: string }[],
  userMembershipId?: number | null,
  membershipName?: string | null,
): Set<number> {
  const ids = new Set<number>();
  for (const m of memberships) {
    if (normalizeMembershipKey(m.name) === "youth") ids.add(m.id);
  }
  if (membershipName && normalizeMembershipKey(membershipName) === "youth") {
    if (userMembershipId != null && userMembershipId > 0) {
      ids.add(userMembershipId);
    }
  }
  return ids;
}

function canAccessEventMembership(
  allowedMembershipIds: string | null | undefined,
  membershipId: number,
  youthTierMembershipIds: ReadonlySet<number>,
): boolean {
  const allowed = parseAllowedMembershipIds(allowedMembershipIds);
  if (allowed.includes(membershipId)) return true;
  if (
    youthTierMembershipIds.size > 0 &&
    youthTierMembershipIds.has(membershipId) &&
    allowed.some((id) => youthTierMembershipIds.has(id))
  ) {
    return true;
  }
  return false;
}

/** Matches API ResolveMembershipIdAsync: requires hasPaid=1, then MembershipName, then MembershipId. */
export function resolveEffectiveMembershipId(
  membershipId?: number | null,
  membershipName?: string | null,
  memberships?: readonly { id: number; name: string }[],
  hasPaid?: number | null,
): number | null {
  if (hasPaid !== 1) return null;
  const name = membershipName?.trim();
  if (name && memberships?.length) {
    const match = findMembershipByUserName(memberships, name);
    if (match) return match.id;
  }
  if (membershipId != null && membershipId > 0) return membershipId;
  return null;
}

/** True while membership list is loading and name-based access cannot be evaluated yet. */
export function isMembershipAccessCheckPending(
  allowedMembershipIds: string | null | undefined,
  membershipName?: string | null,
  memberships?: readonly { id: number; name: string }[],
  hasPaid?: number | null,
): boolean {
  if (isEventOpenToAllMemberships(allowedMembershipIds)) return false;
  if (hasPaid !== 1) return false;
  if (!membershipName?.trim()) return false;
  return !memberships?.length;
}

export function canUserViewEvent(
  allowedMembershipIds: string | null | undefined,
  userMembershipId?: number | null,
  options?: {
    membershipName?: string | null;
    memberships?: readonly { id: number; name: string }[];
    hasPaid?: number | null;
  },
): boolean {
  if (isEventOpenToAllMemberships(allowedMembershipIds)) return true;
  const effectiveId = resolveEffectiveMembershipId(
    userMembershipId,
    options?.membershipName,
    options?.memberships,
    options?.hasPaid,
  );
  if (!effectiveId || effectiveId <= 0) return false;
  const youthTierIds = getYouthTierMembershipIds(
    options?.memberships ?? [],
    userMembershipId,
    options?.membershipName,
  );
  return canAccessEventMembership(
    allowedMembershipIds,
    effectiveId,
    youthTierIds,
  );
}

export function defaultSelectedMembershipIds(
  memberships: { id: number }[],
  allowedMembershipIds?: string | null,
): number[] {
  const parsed = parseAllowedMembershipIds(allowedMembershipIds);
  if (parsed.length > 0) return parsed;
  return memberships.map((m) => m.id);
}
